// @flow
import React, { Component } from "react";
import axios from "axios";
import debounce from "lodash/debounce";
import Dropdown from "./Dropdown";
import Bath from "../icons/Bath";
import Bed from "../icons/Bed";
import LivingArea from "../icons/LivingArea";
import Price from "../icons/Price";
import LotSize from "../icons/LotSize";
import SoldDateCalendar from "../icons/SoldDateCalendar";
import YearBuilt from "../icons/YearBuilt";
import HighConfidence from "../icons/HighConfidence";
import MidConfidence from "../icons/MidConfidence";
import LowConfidence from "../icons/LowConfidence";
import type { $AttomData } from "server/web/controllers/seller-lead-ctrl";

type $Props = {
  children: any,
  city: string,
  state: string,
  geoCodeUrl: string,
};

type $AddressLabel = string;

type $Address = { street: string, placeId: string };

type $GoogleAddress = {
  label: $AddressLabel,
  value: $Address,
};

type $State = {
  error: boolean,
  addressLoading: boolean,
  page: string,
  location: { lat: number, lng: number },
  addresses: Array<$GoogleAddress>,
  address: $GoogleAddress,
  lat: number,
  lng: number,
  avm: Object,
  leadId: ?number,
  addressChosen: boolean,
  latitude?: number,
  longitude?: number,
  invalidAddress: boolean,
};

type $SellerLeadProfile = {
  name: string,
  email: string,
  phone: string,
};

type $SellerLeadProfileExtented = {
  address: $Address,
  name: string,
  email: string,
  phone: string,
  intent: string,
  homeCondition: string,
};

type $SellerLeadContext = {
  page: string,
  location: { lat: number, lng: number },
  address: Object,
  city: string,
  state: string,
  avm: $AttomData,
  handlePage1: () => Promise<void>,
  handlePage2: (values: $SellerLeadProfile) => Promise<void>,
  handlePage3: (values: $SellerLeadProfileExtented) => Promise<void>,
  maybeAddressSearch: (current: any, next: any) => void,
  toggleAddressChosen: () => void,
  renderAddresses: () => any,
  renderLoading: () => any,
  renderIcon: (iconName: string) => mixed,
  navigateToThankYou: () => void,
  emailAgent: (buttonName: string) => Promise<void>,
  invalidAddress: boolean,
};

const SellerLeadContext: React$Context<
  $SellerLeadContext
> = React.createContext(
  ({
    page: "",
    location: {},
    address: {},
    city: "",
    state: "",
    geoCodeUrl: "",
    handlePage1: () => new Promise((resolve) => resolve()),
    handlePage2: () => new Promise((resolve) => resolve()),
    handlePage3: () => new Promise((resolve) => resolve()),
    maybeAddressSearch: () => undefined,
    renderAddresses: () => null,
    renderLoading: () => null,
    renderIcon: () => {},
    navigateToThankYou: () => undefined,
    toggleAddressChosen: () => undefined,
    onInputChange: () => undefined,
    avm: {},
    emailAgent: () => new Promise((resolve) => resolve()),
    invalidAddress: false,
  }: $SellerLeadContext)
);

class SellerLeadProvider extends Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      error: false,
      addressLoading: false,
      page: "sellerPage1",
      location: {},
      addresses: [],
      address: {},
      lat: 0,
      lng: 0,
      avm: {},
      leadId: null,
      addressChosen: false,
      invalidAddress: false,
    };
  }

  componentDidMount = async () => {
    await axios({
      method: "post",
      url: this.props.geoCodeUrl,
    }).then(({ data }) => {
      const { latitude, longitude } = data;
      this.setState({ latitude, longitude });
    });
  };

  // // Function for rendering statistic icons
  renderIcon = (iconName: string): mixed => {
    switch (iconName) {
      case "Bed":
        return <Bed />;
      case "Bath":
        return <Bath />;
      case "LivingArea":
        return <LivingArea />;
      case "Price":
        return <Price />;
      case "LotSize":
        return <LotSize />;
      case "SoldDateCalendar":
        return <SoldDateCalendar />;
      case "YearBuilt":
        return <YearBuilt />;
      case "Low":
        return <LowConfidence />;
      case "Mid":
        return <MidConfidence />;
      case "High":
        return <HighConfidence />;
      default:
        return null;
    }
  };

  trackActions = async (): Promise<void> => {
    if (window.fbq) {
      // track Facebook event for `CompleteRegistration`
      window.fbq("track", "CompleteRegistration");
    }
    if (window.gtag && window.trackGoogleConversion) {
      // track Google event 'conversion'
      // function needs to be created as a global to trigger
      window.trackGoogleConversion();
    }
    if (window.uetq) {
      // track Bing event for seller lead conversion
      window.uetq.push("event", "submit_seller_lead_form", {});
    }
    await this.trackBuyerlinkConversion();
  };

  trackBuyerlinkConversion = async (): Promise<void> => {
    const Token = window.sessionStorage.getItem("bltoken");
    const ClickId = window.sessionStorage.getItem("blclickid");
    const UserId = window.sessionStorage.getItem("bluserid");
    const Position = window.sessionStorage.getItem("blclickpos");
    if (Token && ClickId && UserId && Position) {
      await axios.post("https://trk.buyerlink.com/api/conversion/track", {
        Token,
        ClickId,
        UserId,
        Position,
      });
    }
  };

  emailAgent = async (buttonName: string): Promise<void> => {
    // emails the agent when button is clicked is called from CTA
    const { leadId } = this.state;
    const { state } = this.props;
    return axios({
      method: "post",
      url: `${state}/email`,
      data: { leadId, buttonName },
    }).then(() => {
      this.navigateToEmailConfirmation();
    });
  };

  addressSearch = async (address: string): any => {
    this.setState({ addressLoading: true });
    // what is our geo locations?
    const { latitude, longitude } = this.state;
    await axios({
      method: "post",
      url: "/api/local/places/autocomplete",
      data: { address, latitude, longitude },
    }).then(({ data }) => {
      this.setState({ addresses: data, addressLoading: false });
    });
  };
  // only render the loading when there are no results
  renderLoading = (): any => {
    if (this.state.addressLoading && !this.state.addresses.length) {
      const address = { label: "Loading..." };
      return <Dropdown handlePage={null} address={address} key={null} />;
    }
  };

  renderAddresses = (): any => {
    const { addresses, addressChosen } = this.state;

    if (!(addresses && addresses.length) || addressChosen) {
      return null;
    }

    return addresses.map((address) => {
      return (
        <Dropdown
          handlePage={this.handleAddressChoose}
          address={address}
          key={address.value.placeId}
        />
      );
    });
  };

  debounceAddressSearch = debounce(
    (address: string) => this.addressSearch(address),
    500
  );

  maybeAddressSearch = (current: any, next: any): void => {
    const currentAddress = current.values.address;
    const nextAddress = next.values.address;

    if (currentAddress !== nextAddress) {
      this.debounceAddressSearch(nextAddress);
    }
  };

  fetchGoogleMapCoordinates = async (placeId: string): Promise<void> => {
    // fetch Google Map Coordinates, set the location
    await axios({
      method: "post",
      url: "/api/local/places/details",
      data: { placeId },
    }).then(({ data: { geometry: { location: { lat, lng } } } }) => {
      const location = { lat, lng };
      this.setState({ location });
    });
  };

  createLead = async (address: string): Promise<void> => {
    await axios({
      method: "post",
      url: "",
      data: { address },
    }).then(({ data: { avm, leadId } }) => {
      this.setState({ avm, leadId });
    });
  };

  toggleAddressChosen = (): void => {
    this.setState({ addressChosen: false, invalidAddress: false });
  };

  handleAddressChoose = (address: any) => {
    this.setState({ addressChosen: true, address });
  };

  // page 1 handler
  // upsert bare bones lead, and grab map data for display
  handlePage1 = async (): Promise<void> => {
    const { address } = this.state;
    if (address && address.label) {
      // track capture event
      await this.trackActions();
      await this.fetchGoogleMapCoordinates(address.value.placeId);
      this.setState({ page: "sellerPage2", address });
      await this.createLead(address.label);
    } else {
      this.setState({
        invalidAddress: true,
        addressChosen: false,
        addresses: [],
      });
    }
  };

  // update name, email and phone number
  // we also create a site profile and password for the individual
  handlePage2 = async (values: $SellerLeadProfile): Promise<void> => {
    await this.sellerLeadsUpsert(values);
    this.setState({
      page: "sellerPage3",
    });
  };

  // upsert remaining values for survey
  handlePage3 = async (values: $SellerLeadProfileExtented): Promise<void> => {
    await this.sellerLeadsUpsert(values);
    this.navigateToThankYou();
  };
  // Navigates to seller thank you
  navigateToThankYou = (): void => {
    this.setState({ page: "sellerPageThankyou" });
  };

  navigateToEmailConfirmation = (): void => {
    this.setState({ page: "sellerPageEmailConfirmation" });
  };

  // upsert data to a lead
  sellerLeadsUpsert = (values: Object): number => {
    const { leadId } = this.state;
    return axios({
      method: "put",
      url: "",
      data: { ...values, leadId },
    });
  };

  render() {
    const { page, location, avm, address, invalidAddress } = this.state;

    const { city, state, geoCodeUrl } = this.props;

    return (
      // @TODO clean this up
      <SellerLeadContext.Provider
        value={{
          page,
          handlePage1: this.handlePage1,
          handlePage2: this.handlePage2,
          handlePage3: this.handlePage3,
          maybeAddressSearch: this.maybeAddressSearch,
          renderAddresses: this.renderAddresses,
          renderLoading: this.renderLoading,
          emailAgent: this.emailAgent,
          renderIcon: this.renderIcon,
          navigateToThankYou: this.navigateToThankYou,
          toggleAddressChosen: this.toggleAddressChosen,
          location,
          avm,
          address,
          city,
          state,
          geoCodeUrl,
          buttonColor: "#F46800",
          invalidAddress,
        }}
      >
        {this.props.children}
      </SellerLeadContext.Provider>
    );
  }
}

// Cannot Strongly Type `Component` Object in any known way
const withSellerLeadContext = (Component: Object) => (props: *) => (
  <SellerLeadContext.Consumer>
    {(sellerLeadContext) => <Component {...sellerLeadContext} {...props} />}
  </SellerLeadContext.Consumer>
);

module.exports = {
  SellerLeadProvider,
  withSellerLeadContext,
};
